import { AfterViewInit, Component, computed, effect, ElementRef, input, OnInit, signal, Signal, viewChild, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, fromEvent, map, startWith } from 'rxjs';
import { EExclusiveStatus, IPortfolioOverviewDisplayed } from 'src/models/products.model';
import { PricePipe } from 'src/pipes/price.pipe';
import { TrimTagNamePipe } from 'src/pipes/trim-tag-name.pipe';
import { STYLE_VALUES_PASSED_VIA_HTML } from 'src/util/constants';

interface ITagConfig {
  readonly defaultWidth: number, // px
  readonly extendedWidth?: number, // px, exclusive tag only
  readonly trimmedWidth: number, // px
  visible: boolean, // visible at actual resolution
  exists: boolean, // visible with enough space
}

@Component({
  selector: 'liberro-investment-badges',
  standalone: false,
  templateUrl: './liberro-investment-badges.component.html',
  styleUrl: './liberro-investment-badges.component.less'
})
export class LiberroInvestmentBadgesComponent implements OnInit, AfterViewInit {
  private translate = inject(TranslateService);
  private pricePipe = inject(PricePipe);
  private trimTagNamePipe = inject(TrimTagNamePipe);

  public investmentInput = input.required<IPortfolioOverviewDisplayed>();
  public exclusiveStatus?: EExclusiveStatus;

  public trimTagNames = signal(false);
  public isInDetail = input<boolean>(false);
  public initialCalcDone = signal(false);

  public dipTooltipVisible = signal<boolean>(false);
  public efektikaTooltipVisible = signal<boolean>(false);
  public exclusiveTooltipVisible = signal<boolean>(false);
  public hiddenTagsTooltipVisible = signal<boolean>(false);

  public hiddenTags = computed(() =>{
    this.hiddenTagsCount(); //keep for trigger
    return [
      this.availableTags['dip'].exists && !this.availableTags['dip'].visible ?
        this.translate.instant('badge.dip') : null,
      this.availableTags['exclusive'].exists && !this.availableTags['exclusive'].visible ?
        this.translate.instant('badge.exclusive.label') : null,
      this.availableTags['efektika'].exists && !this.availableTags['efektika'].visible ?
        this.translate.instant('badge.efektika') : null,
    ].filter(Boolean).join(', ')
  });

  private resolutionChange: Signal<{
    width: number;
    height: number;
  }> = signal({ width: NaN, height: NaN });

  private tagsContainer = viewChild<ElementRef<HTMLDivElement>>('tagsContainer');

  // ordered, hidden for low resolutions from latest to first
  public availableTags: Record<string, ITagConfig> = {
    dip: {
      defaultWidth: 46,
      trimmedWidth: 46,
      visible: true,
      exists: false,
    },
    exclusive: {
      defaultWidth: 79, // eg. "Exclusive"
      extendedWidth: 206, // eg. "Exclusive: 100 000 Kč"
      trimmedWidth: 51, // eg. "Exc."
      visible: true,
      exists: false,
    },
    efektika: {
      defaultWidth: 73,
      trimmedWidth: 49,
      visible: true,
      exists: false,
    },
  };

  public hiddenTagsCount = signal(0);

  public constructor() {
    this.initTagWatcher();
  }

  public ngAfterViewInit(): void {
    this.initTags();
  }

  public ngOnInit(): void {
    if(this.investmentInput().exclusive) {
      this.handleExclusiveStatus();
    }
  }

  private initTags(): void {
    this.initialCalcDone.set(false);
    const investment = this.investmentInput();

    this.availableTags['dip'].visible = !!investment.isDip;
    this.availableTags['efektika'].visible = !!investment.isEfektika;
    this.availableTags['exclusive'].visible = !!investment.exclusive;
    this.availableTags['dip'].exists = !!investment.isDip;
    this.availableTags['efektika'].exists = !!investment.isEfektika;
    this.availableTags['exclusive'].exists = !!investment.exclusive;

    this.updateTagsView();
  }

  private initTagWatcher(): void {
    const resize = fromEvent(window, 'resize').pipe(
      debounceTime(100),
      startWith(null),
      distinctUntilChanged(),
      map(() => ({ width: window.innerWidth, height: window.innerHeight }))
    );
    this.resolutionChange = toSignal(resize, { initialValue: { width: window.innerWidth, height: window.innerHeight } });

    effect(() => this.updateTagsView());
  }

  private updateTagsView(): void {
    this.resolutionChange(); //keep for trigger

    const containerResolutions = this.tagsContainer()?.nativeElement?.getBoundingClientRect();
    const styles = getComputedStyle(document.documentElement);

    const failsafeOffset = 0.95; // leave 5% offset

    const elementPadding = styles.getPropertyValue(STYLE_VALUES_PASSED_VIA_HTML.INVESTMENT_TAGS_PADDING);
    const portfolioTags = Object.values(this.availableTags).filter(x => x.exists);

    const totalTagsLength = this.getTotalTagsLength();
    const trimmedTagsLength = portfolioTags.reduce((sum, x) => sum + x.trimmedWidth, 0);
    const availableSpaceForTags = (containerResolutions?.width ?? 0 - (parseInt(elementPadding) * portfolioTags.length)) * failsafeOffset;

    if(totalTagsLength > availableSpaceForTags) {
      this.trimTagNames.set(true);
    } else {
      this.trimTagNames.set(false);
      for (const key of Object.keys(this.availableTags)) {
        this.availableTags[key].visible = true;
      }
    }

    this.handleHiddenTags(trimmedTagsLength, availableSpaceForTags);
    this.initialCalcDone.set(true);
  }

  private getTotalTagsLength(): number {
    let sum = 0;
    Object.entries(this.availableTags)
      .filter(([tagName, value]) => value.exists)
      .forEach(([tagName, value]) => {
        if(tagName === 'exclusive') {
          sum += (this.exclusiveStatus !== EExclusiveStatus.COMPLETED ?
            (value.extendedWidth ?? value.defaultWidth) :
            value.defaultWidth);
        } else {
          sum += value.defaultWidth;
        }
      }
    );

    return sum;
  }

  private handleHiddenTags(trimmedTagsLength: number, availableSpaceForTags: number) {
    const nTagWidth = 43; //px

    if (trimmedTagsLength > availableSpaceForTags) {
      const availableSpace = availableSpaceForTags - nTagWidth;

      let accumulated = 0;

      for (const key of Object.keys(this.availableTags)) {

        accumulated += this.availableTags[key].trimmedWidth;
        this.availableTags[key].visible = accumulated <= availableSpace;
      }

      const totalTags = Object.values(this.availableTags).filter(x => x.exists).length;
      const visibleTags = Object.values(this.availableTags).filter(x => x.exists && x.visible).length;

      this.hiddenTagsCount.set(totalTags - visibleTags);
    } else {
      for (const key of Object.keys(this.availableTags)) {
        this.availableTags[key].visible = true;
      }
      this.hiddenTagsCount.set(0);
    }
  }

  // todo - exclusive conditions should be at one place, export from these changes when merged
  // https://gitlab.com/dfcz/bwm-frontend/-/merge_requests/339/diffs#10d0ffcde14b21f616d30d64d6a3979e3a07176e_102_120
  private handleExclusiveStatus() {
    const exclusiveThreshold = this.investmentInput().exclusiveThreshold;
    if(exclusiveThreshold?.thresholdReachedDate) {
      this.exclusiveStatus = EExclusiveStatus.COMPLETED;
    } else if(
      (!exclusiveThreshold?.daysRemaining && !exclusiveThreshold?.thresholdReachedDate) ||
      this.investmentInput()?.isInLiquidation
    ) {
      this.exclusiveStatus = EExclusiveStatus.LIQUIDATION;
    } else {
      this.exclusiveStatus = EExclusiveStatus.IN_PROGRESS;
    }
  }

  public dipTooltipToggle(visible: boolean, event?: Event): void {
    this.dipTooltipVisible.set(visible);
    event?.stopPropagation();
  }

  public efektikaTooltipToggle(visible: boolean, event?: Event): void {
    this.efektikaTooltipVisible.set(visible);
    event?.stopPropagation();
  }

  public exclusiveTooltipToggle(visible: boolean, event?: Event): void {
    this.exclusiveTooltipVisible.set(visible);
    event?.stopPropagation();
  }

  public hiddenTagsTooltipToggle(visible: boolean, event?: Event): void {
    this.hiddenTagsTooltipVisible.set(visible);
    event?.stopPropagation();
  }

  public exclusiveBadgeLabel = computed(() => {
    const baseLabel = this.translate.instant('badge.exclusive.label');
    const statusLabel = this.translate.instant(`badge.exclusive.${this.exclusiveStatus}`);

    let result: string

    switch (this.exclusiveStatus) {
      case EExclusiveStatus.COMPLETED:
        result = `${baseLabel}${statusLabel}`
        break
      case EExclusiveStatus.IN_PROGRESS: {
        const remaining = this.investmentInput().exclusiveThreshold?.remainingAmount ?? 0
        const price = this.pricePipe.transform(remaining)
        result = `${baseLabel}: ${statusLabel} ${price}`
        break
      }
      default:
        result = `${baseLabel}: ${statusLabel}`
    }

    return this.trimTagNamePipe.transform(result, this.trimTagNames());
  })
}
